import type { ReactElement } from 'react';
import { Badge, Box, Flex, Group, SimpleGrid, Text } from '@mantine/core';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { Area, AreaChart, CartesianGrid, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import StatBand from '@/design/StatBand';
import { SurfaceCard } from '@/design/SurfaceCard';
import InsightsRadarChart from './InsightsRadarChart';
import InsightsScoreRing from './InsightsScoreRing';
import type { CohortKind, CohortStanding, ExecutiveScorecard, PeerStanding } from './InsightsTypes';

interface InsightsScorecardSectionProps {
    scorecard: ExecutiveScorecard;
    standing: PeerStanding | null;
    /** The globally-selected peer cohort the radar overlays against (from the page header). */
    cohortKind: CohortKind;
    /** Prior-period deltas are only meaningful for rolling presets; hidden for custom ranges. */
    showDelta: boolean;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
/** "2026-07-12" → "Jul 12" (string split, so no Date/timezone drift). */
function shortDate(iso: string): string {
    const [, month, day] = iso.split('-');
    return month && day ? `${MONTHS[Number(month) - 1]} ${Number(day)}` : iso;
}

function DeltaChip({ delta, suffix }: { delta: number; suffix: string }): ReactElement {
    const color = delta >= 0 ? 'var(--bm-color-success)' : 'var(--bm-color-danger)';
    const Icon = delta >= 0 ? TrendingUp : TrendingDown;
    return (
        <Group gap={4} align="center">
            <Icon size={13} color={color} />
            <Text size="xs" fw={700} style={{ color }}>
                {delta >= 0 ? '+' : ''}
                {delta} {suffix}
            </Text>
        </Group>
    );
}

/** One peer-cohort card: rank/percentile headline, cohort average, and the change in that average this period. */
function StandingCard({ standing, showDelta }: { standing: CohortStanding; showDelta: boolean }): ReactElement {
    const showsRank = standing.kind !== 'department';
    const eyebrow = standing.kind === 'department' ? 'Your department' : standing.label;
    const headline = showsRank ? `#${standing.rank} of ${standing.size}` : `Top ${standing.topPercent}% of ${standing.label}`;
    const badge = showsRank ? `#${standing.rank} of ${standing.size}` : `Top ${standing.topPercent}%`;
    return (
        <SurfaceCard $padding="md">
            <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Box style={{ minWidth: 0 }}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={4} style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                        {eyebrow}
                    </Text>
                    <Text fw={700} size="md" style={{ lineHeight: 1.25 }}>
                        {headline}
                    </Text>
                    <Text size="sm" c="dimmed" mt={2}>
                        {standing.label} averages {standing.avgFluency} fluency
                    </Text>
                </Box>
                <Badge variant="light" style={{ flexShrink: 0 }}>
                    {badge}
                </Badge>
            </Group>
            {showDelta && (
                <Box mt="sm">
                    <DeltaChip delta={standing.avgDelta} suffix="pts this period" />
                </Box>
            )}
        </SurfaceCard>
    );
}

/** Scorecard — composite AI-fluency score, sub-ratings, the fluency radar vs the selected peer cohort, and per-cohort standing cards. */
export default function InsightsScorecardSection({ scorecard, standing, cohortKind, showDelta }: InsightsScorecardSectionProps): ReactElement {
    const cohorts = [standing?.team, standing?.department, standing?.company].filter((c): c is CohortStanding => c != null);
    const activeCohort = (standing?.[cohortKind] ?? cohorts[0]) ?? null;
    const cohortLabel = activeCohort ? `${activeCohort.label} average` : undefined;
    const trendAccent = { solid: 'var(--bm-color-brand-accent)' };
    const lastTrend = scorecard.fluencyTrend[scorecard.fluencyTrend.length - 1];

    return (
        <SurfaceCard $padding="lg">
            <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl" style={{ alignItems: 'center' }}>
                <Flex align="center" justify="center" gap="lg" wrap="wrap">
                <Box style={{ flexShrink: 0 }}>
                    <InsightsScoreRing
                        score={scorecard.fluencyScore}
                        label="Fluency"
                        accent="indigo"
                        sublabel={showDelta ? <DeltaChip delta={scorecard.fluencyDelta} suffix="vs prior" /> : undefined}
                    />
                </Box>
                {scorecard.fluencyTrend.length > 0 && (
                    <Box style={{ flex: '1 1 200px', maxWidth: 300, minWidth: 180 }}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6} style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Fluency over time
                        </Text>
                        <ResponsiveContainer width="100%" height={140}>
                            <AreaChart data={scorecard.fluencyTrend} margin={{ top: 8, right: 10, bottom: 0, left: 4 }}>
                            <defs>
                                <linearGradient id="scorecard-trend" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={trendAccent.solid} stopOpacity={0.35} />
                                    <stop offset="100%" stopColor={trendAccent.solid} stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid vertical={false} stroke="var(--bm-color-border-subtle)" />
                            <XAxis
                                dataKey="date"
                                tickFormatter={shortDate}
                                tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 9 }}
                                tickLine={false}
                                axisLine={false}
                                minTickGap={26}
                            />
                            <YAxis
                                domain={[0, 100]}
                                width={30}
                                tick={{ fill: 'var(--bm-color-text-dimmed)', fontSize: 9 }}
                                tickLine={false}
                                axisLine={false}
                            />
                            <Tooltip
                                formatter={(value: number) => [`${value} / 100`, 'Score']}
                                labelFormatter={(label: string) => shortDate(label)}
                                contentStyle={{ background: 'var(--bm-color-surface)', border: '1px solid var(--bm-color-border)', borderRadius: 'var(--bm-radius)', fontSize: 12 }}
                            />
                            <Area
                                type="monotone"
                                dataKey="fluency"
                                stroke={trendAccent.solid}
                                strokeWidth={2.5}
                                fill="url(#scorecard-trend)"
                                dot={{ r: 2.5, fill: trendAccent.solid, strokeWidth: 0, opacity: 0.6 }}
                                activeDot={{ r: 4, fill: trendAccent.solid, stroke: 'var(--bm-color-surface)', strokeWidth: 2 }}
                            />
                            {lastTrend && (
                                <ReferenceDot
                                    x={lastTrend.date}
                                    y={lastTrend.fluency}
                                    r={5}
                                    fill={trendAccent.solid}
                                    stroke="var(--bm-color-surface)"
                                    strokeWidth={2}
                                    isFront
                                />
                            )}
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}
                </Flex>
                <Flex justify="center">
                    <Box style={{ width: '100%', maxWidth: 380 }}>
                        <Text size="xs" c="dimmed" fw={700} tt="uppercase" mb={6} style={{ fontSize: '9px', letterSpacing: '0.08em' }}>
                            Fluency by dimension{cohortLabel ? ` — you vs ${activeCohort?.label ?? ''}` : ''}
                        </Text>
                        <InsightsRadarChart user={scorecard.radar} cohort={activeCohort?.axisAverages} cohortLabel={cohortLabel} accent="indigo" />
                    </Box>
                </Flex>
            </SimpleGrid>

            <Box mt="lg">
                <StatBand
                    stats={scorecard.radar.map((axis) => ({ label: axis.axis, value: `${axis.user}`, hint: 'out of 100' }))}
                />
            </Box>

            {cohorts.length > 0 && (
                <SimpleGrid cols={{ base: 1, md: Math.min(cohorts.length, 3) }} spacing="md" mt="lg">
                    {cohorts.map((cohort) => (
                        <StandingCard key={cohort.kind} standing={cohort} showDelta={showDelta && cohort.kind !== 'company'} />
                    ))}
                </SimpleGrid>
            )}
        </SurfaceCard>
    );
}
